// src/context/AuthContext.js
// Robust auth context with race condition protection and session persistence
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [authEvent, setAuthEvent] = useState(null); // Track latest auth event

  useEffect(() => {
    let mounted = true;
    // 1. Get initial session first (prevents flash)
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!mounted) return;
      if (error) console.error('Session error:', error);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // 2. Listen for all auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
      setAuthEvent(event);
      // Clean URL hash after confirmation
      if (event === 'SIGNED_IN' && window.location.hash?.includes('access_token')) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = useCallback(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      return { data, error };
    } catch (e) {
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  }, []);

  const signIn = useCallback(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      return { data, error };
    } catch (e) {
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      // Force clear even if network fails
      setUser(null);
    }
  }, []);

  const resendConfirmation = useCallback(async (email) => {
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      return { error };
    } catch (e) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, authEvent, signUp, signIn, signOut, resendConfirmation }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
